import { defineRecipe } from '@plurall/elo-v3';

export const scrollTabsPanelRootRecipe = defineRecipe({
  className: 'scroll-tabs-panel',
  base: {
    display: 'flex',
    gap: 'space.8',
    height: '100%',
    flexDirection: 'column',
  },
});

export const scrollTabsPanelControlsRecipe = defineRecipe({
  className: 'scroll-tabs-panel-controls',
  base: {
    position: 'relative',
    display: 'grid',
    gridTemplateColumns: '2.5rem 1fr 2.5rem',
    gridTemplateAreas: "'prev middle next'",
  },
});

export const scrollTabsPanelPrevRecipe = defineRecipe({
  className: 'scroll-tabs-panel-prev',
  base: {
    gridArea: 'prev',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    position: 'absolute',
    inset: 0,
    pointerEvents: 'none',
    zIndex: 1,
    backgroundImage: 'linear-gradient(90deg, rgba(255, 255, 255, 1) 1.25rem, rgba(255, 255, 255, 0))',
  },
});

export const scrollTabsPanelNextRecipe = defineRecipe({
  className: 'scroll-tabs-panel-next',
  base: {
    gridArea: 'next',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    position: 'absolute',
    inset: 0,
    pointerEvents: 'none',
    zIndex: 1,
    backgroundImage: 'linear-gradient(270deg, rgba(255, 255, 255, 1) 1.25rem, rgba(255, 255, 255, 0))',
  },
});

export const scrollTabsPanelNavButtonRecipe = defineRecipe({
  className: 'scroll-tabs-panel-nav-button',
  base: {
    position: 'relative',
    border: 'none',
    backgroundColor: 'rgba(255, 255, 255, 0)',
    aspectRatio: '1',
    borderRadius: '50%',
    display: 'inline-flex',
    justifyContent: 'center',
    alignItems: 'center',
    overflow: 'clip',
    cursor: 'pointer',
    pointerEvents: 'auto',
    isolation: 'isolate',
    padding: 0,
    width: '2rem',
    height: '2rem',

    _before: {
      content: '""',
      position: 'absolute',
      inset: 0,
      borderRadius: '50%',
      backgroundColor: 'rgba(0, 0, 0, 0.1)',
      opacity: 0,
      transition: 'opacity 150ms ease-in-out',
      zIndex: -1,
    },

    _hover: { _before: { opacity: 1 } },
    _focusVisible: { _before: { opacity: 1 } },
  },
});

export const scrollTabsPanelTabsRecipe = defineRecipe({
  className: 'scroll-tabs-panel-tabs',
  base: {
    gridColumn: '1 / -1',
    whiteSpace: 'nowrap',
    overflowX: 'auto',
    scrollbarWidth: 'none',
    '&::-webkit-scrollbar': { display: 'none' },

    '@media (prefers-reduced-motion: no-preference)': {
      scrollBehavior: 'smooth',
    },
  },
});

export const scrollTabsPanelListRecipe = defineRecipe({
  className: 'scroll-tabs-panel-list',
  base: {
    listStyle: 'none',
    paddingInline: 0,
    paddingBlock: 'space.8',
    marginBlockEnd: '-8px',
    margin: 0,
    display: 'flex',
    flexDirection: 'row',
    gap: 'space.4',
  },
});

export const scrollTabsPanelButtonRecipe = defineRecipe({
  className: 'scroll-tabs-panel-button',
  base: {
    display: 'inline-block',
    font: 'inherit',
    paddingInline: '1em',
    paddingBlock: '0.5em',
    borderRadius: '100vw',
    caretColor: 'transparent',
    backgroundColor: 'background.primary',
    color: 'text.primary',
    border: '0.125em solid {colors.border.tertiary}',
    scrollSnapAlign: 'center',
    transition: 'border-color 150ms ease-in-out',
    cursor: 'pointer',
    textAlign: 'center',

    '&[aria-selected="true"]': {
      borderColor: 'action.focus.ring.primary',
    },
    _hover: {
      borderColor: 'action.focus.ring.primary',
    },
    _focusVisible: {
      outline: '0.125em solid {colors.background.primary}',
      boxShadow: '0 0 0 0.25em {colors.action.focus.ring.primary}',
    },

    'li:last-of-type &': {
      marginInlineEnd: 'space.16',
    },
  },
});

export const scrollTabsPanelScrollableRecipe = defineRecipe({
  className: 'scroll-tabs-panel-scrollable',
  base: {
    flex: '1 1 auto',
    overflow: 'auto',
    overscrollBehavior: 'auto',
    paddingInlineEnd: 'space.8',
    display: 'flex',
    flexDirection: 'column',
    gap: 'space.16',

    _focusVisible: {
      outline: '0.125em solid {colors.action.focus.ring.primary}',
      outlineOffset: 'space.8',
    },
    '&:focus-within': {
      outline: '0.125em solid {colors.action.focus.ring.primary}',
      outlineOffset: 'space.8',
    },

    '@media (prefers-reduced-motion: no-preference)': {
      scrollBehavior: 'smooth',
    },
  },
});
